import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateMetadocMetadataMutation,
  useGetMetadocMetadataQuery,
  useUpdateMetadocMetadataMutation,
} from "services/metadoc-api";

import { parseMetadataValue } from "../../utils/codeMetadataUtils";

import type { TabId } from "../../metadoc.types";

type UseAIInsightDocumentArgs = {
  metadocId: number;
  tabId: TabId;
  activeDocumentGroupId?: number;
  shouldFetch: boolean;
};

type UseAIInsightDocumentReturn = {
  content: null | string;
  editorValue: string;
  date: null | string;
  dateValue: string;
  metadataId: number | null;
  isLoading: boolean;
  isFetching: boolean;
  isCreating: boolean;
  canCreate: boolean;
  onChange: (value: string) => void;
  onBlurSave: () => Promise<void>;
  onDateChange: (value: string) => void;
  onDateBlurSave: () => Promise<void>;
  onCreate: () => Promise<void>;
};

type SelectedInsightDocument = {
  content: null | string;
  metadataId: number | null;
  rawValue?: null | string | Record<string, unknown>;
  date?: string | null;
};

type InsightMetadataRow = {
  document_group: null | number;
  id: number;
  value: null | string | Record<string, unknown>;
  date?: string | null;
};

type InsightDocumentSelectorArgs = {
  activeDocumentGroupId?: number;
  insightMetadata: InsightMetadataRow[];
};

type InsightDocumentSelector = (
  args: InsightDocumentSelectorArgs,
) => SelectedInsightDocument;

const EXECUTIVE_SUMMARY_TAB = "executive-summary";
const PER_RECORD_TAB = "per-record";
const MEDICATIONS_TAB = "medications";
const CURRENT_STATUS_TAB = "current-status";
const CURRENT_STATUS_ACCEPTED_TAB = "current-status-accepted";
const CURRENT_STATUS_DENIED_TAB = "current-status-denied";
const WARNINGS_ACCEPTED_TAB = "warnings-accepted";
const WARNINGS_DENIED_TAB = "warnings-denied";
const COST_MITIGATIONS_ACCEPTED_TAB = "cost-mitigations-accepted";
const COST_MITIGATIONS_DENIED_TAB = "cost-mitigations-denied";
const MEDICATION_TABS: TabId[] = [MEDICATIONS_TAB];
const OTHER_SUMMARY_TABS: TabId[] = [
  CURRENT_STATUS_TAB,
  CURRENT_STATUS_ACCEPTED_TAB,
  CURRENT_STATUS_DENIED_TAB,
  WARNINGS_ACCEPTED_TAB,
  WARNINGS_DENIED_TAB,
  COST_MITIGATIONS_ACCEPTED_TAB,
  COST_MITIGATIONS_DENIED_TAB,
];
const MAX_TIPTAP_DOC_CONTENT_NODES = 250;

const isTabOfType = (tabId: TabId, types: TabId[]): boolean =>
  types.includes(tabId);

const isMedicationTab = (tabId: TabId): boolean =>
  isTabOfType(tabId, MEDICATION_TABS);
const isOtherSummaryTab = (tabId: TabId): boolean =>
  isTabOfType(tabId, OTHER_SUMMARY_TABS);

type TiptapMedicationSummaryDoc = {
  type: "doc";
  content: Record<string, unknown>[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasBoundedArraySize = <T>(
  value: unknown,
  maxSize: number,
  itemGuard: (item: unknown) => item is T,
): value is T[] =>
  Array.isArray(value) && value.length <= maxSize && value.every(itemGuard);

const parseJsonRecord = (
  value: null | string | Record<string, unknown>,
): Record<string, unknown> | null => {
  if (!value) {
    return null;
  }

  const parsedValue = parseMetadataValue(value);
  return isRecord(parsedValue) ? parsedValue : null;
};

const isTiptapMedicationSummaryDoc = (
  value: Record<string, unknown>,
): value is TiptapMedicationSummaryDoc =>
  value.type === "doc" &&
  hasBoundedArraySize<Record<string, unknown>>(
    value.content,
    MAX_TIPTAP_DOC_CONTENT_NODES,
    isRecord,
  );

const getTipTapMedicationSummary = (
  value: null | string | Record<string, unknown>,
): TiptapMedicationSummaryDoc | null => {
  if (!value) {
    return null;
  }

  const parsedValue = parseJsonRecord(value);
  if (!parsedValue) {
    return null;
  }

  if (!isTiptapMedicationSummaryDoc(parsedValue)) {
    return null;
  }

  return parsedValue;
};

const EMPTY_INSIGHT_DOCUMENT: SelectedInsightDocument = {
  content: null,
  metadataId: null,
};

const getSelectedStringDocument = (
  metadataRow?: InsightMetadataRow,
): SelectedInsightDocument => {
  const content =
    typeof metadataRow?.value === "string" && metadataRow.value.length > 0
      ? metadataRow.value
      : null;

  return {
    content,
    metadataId: metadataRow?.id ?? null,
  };
};

const selectExecutiveSummaryDocument: InsightDocumentSelector = ({
  insightMetadata,
}) =>
  getSelectedStringDocument(
    insightMetadata.find((metadataRow) => metadataRow.document_group === null),
  );

const selectPerRecordSummaryDocument: InsightDocumentSelector = ({
  activeDocumentGroupId,
  insightMetadata,
}) =>
  getSelectedStringDocument(
    insightMetadata.find(
      (metadataRow) => metadataRow.document_group === activeDocumentGroupId,
    ),
  );

const selectMedicationDocument: InsightDocumentSelector = ({
  activeDocumentGroupId,
  insightMetadata,
}) => {
  if (typeof activeDocumentGroupId !== "number") {
    return EMPTY_INSIGHT_DOCUMENT;
  }

  const medicationMetadata = insightMetadata.find((metadataRow) => {
    if (metadataRow.document_group !== activeDocumentGroupId) {
      return false;
    }

    return !!getTipTapMedicationSummary(metadataRow.value);
  });

  if (!medicationMetadata) {
    return EMPTY_INSIGHT_DOCUMENT;
  }

  return {
    content: JSON.stringify(
      getTipTapMedicationSummary(medicationMetadata.value),
    ),
    metadataId: medicationMetadata.id,
  };
};

const getOtherSummarySubtype = (
  value: null | string | Record<string, unknown>,
): string | null => {
  if (!value) {
    return null;
  }
  try {
    const parsed = parseMetadataValue(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      "type" in parsed
    ) {
      return (parsed as Record<string, unknown>).type as string;
    }
  } catch {
    return null;
  }
  return null;
};

const getOtherSummaryText = (
  value: null | string | Record<string, unknown>,
): string | null => {
  if (!value) {
    return null;
  }
  try {
    const parsed = parseMetadataValue(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      "text" in parsed
    ) {
      return (parsed as Record<string, unknown>).text as string;
    }
  } catch {
    return null;
  }
  return typeof value === "string" ? value : null;
};

const selectCurrentStatusAcceptedDocument: InsightDocumentSelector = ({
  insightMetadata,
}) => {
  const statusEntry = insightMetadata.find((row) => {
    if (row.document_group !== null) {
      return false;
    }
    if (getOtherSummarySubtype(row.value) !== "current_status_of_treatment") {
      return false;
    }

    try {
      const parsed = parseMetadataValue(row.value);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        const conditionType = (parsed as Record<string, unknown>)
          .condition_type;
        return conditionType === "accepted";
      }
    } catch {
      return false;
    }
    return false;
  });

  if (!statusEntry) {
    return EMPTY_INSIGHT_DOCUMENT;
  }

  const content = getOtherSummaryText(statusEntry.value);
  return {
    content,
    metadataId: statusEntry.id,
    rawValue: statusEntry.value,
    date: statusEntry.date ?? null,
  };
};

const selectCurrentStatusDeniedDocument: InsightDocumentSelector = ({
  insightMetadata,
}) => {
  const statusEntry = insightMetadata.find((row) => {
    if (row.document_group !== null) {
      return false;
    }
    if (getOtherSummarySubtype(row.value) !== "current_status_of_treatment") {
      return false;
    }

    try {
      const parsed = parseMetadataValue(row.value);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        const conditionType = (parsed as Record<string, unknown>)
          .condition_type;
        return conditionType === "denied";
      }
    } catch {
      return false;
    }
    return false;
  });

  if (!statusEntry) {
    return EMPTY_INSIGHT_DOCUMENT;
  }

  const content = getOtherSummaryText(statusEntry.value);

  return {
    content,
    metadataId: statusEntry.id,
    rawValue: statusEntry.value,
    date: statusEntry.date ?? null,
  };
};

const getOtherSummaryConditionType = (
  value: null | string | Record<string, unknown>,
): string | null => {
  if (!value) {
    return null;
  }
  try {
    const parsed = parseMetadataValue(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      "condition_type" in parsed
    ) {
      return (parsed as Record<string, unknown>).condition_type as string;
    }
  } catch {
    return null;
  }
  return null;
};

const selectWarningsAcceptedDocument: InsightDocumentSelector = ({
  activeDocumentGroupId,
  insightMetadata,
}) => {
  const metadataRow = insightMetadata.find(
    (row) =>
      row.document_group === activeDocumentGroupId &&
      getOtherSummarySubtype(row.value) === "warning" &&
      getOtherSummaryConditionType(row.value) === "accepted",
  );
  if (!metadataRow) {
    return EMPTY_INSIGHT_DOCUMENT;
  }
  return {
    content: getOtherSummaryText(metadataRow.value),
    metadataId: metadataRow.id,
    rawValue: metadataRow.value,
    date: metadataRow.date ?? null,
  };
};

const selectWarningsDeniedDocument: InsightDocumentSelector = ({
  activeDocumentGroupId,
  insightMetadata,
}) => {
  const metadataRow = insightMetadata.find(
    (row) =>
      row.document_group === activeDocumentGroupId &&
      getOtherSummarySubtype(row.value) === "warning" &&
      getOtherSummaryConditionType(row.value) === "denied",
  );
  if (!metadataRow) {
    return EMPTY_INSIGHT_DOCUMENT;
  }
  return {
    content: getOtherSummaryText(metadataRow.value),
    metadataId: metadataRow.id,
    rawValue: metadataRow.value,
    date: metadataRow.date ?? null,
  };
};

const selectCostMitigationsAcceptedDocument: InsightDocumentSelector = ({
  activeDocumentGroupId,
  insightMetadata,
}) => {
  const metadataRow = insightMetadata.find(
    (row) =>
      row.document_group === activeDocumentGroupId &&
      getOtherSummarySubtype(row.value) === "cost_mitigation" &&
      getOtherSummaryConditionType(row.value) === "accepted",
  );
  if (!metadataRow) {
    return EMPTY_INSIGHT_DOCUMENT;
  }
  return {
    content: getOtherSummaryText(metadataRow.value),
    metadataId: metadataRow.id,
    rawValue: metadataRow.value,
    date: metadataRow.date ?? null,
  };
};

const selectCostMitigationsDeniedDocument: InsightDocumentSelector = ({
  activeDocumentGroupId,
  insightMetadata,
}) => {
  const metadataRow = insightMetadata.find(
    (row) =>
      row.document_group === activeDocumentGroupId &&
      getOtherSummarySubtype(row.value) === "cost_mitigation" &&
      getOtherSummaryConditionType(row.value) === "denied",
  );
  if (!metadataRow) {
    return EMPTY_INSIGHT_DOCUMENT;
  }
  return {
    content: getOtherSummaryText(metadataRow.value),
    metadataId: metadataRow.id,
    rawValue: metadataRow.value,
    date: metadataRow.date ?? null,
  };
};

const insightDocumentSelectors: Partial<
  Record<TabId, InsightDocumentSelector>
> = {
  [EXECUTIVE_SUMMARY_TAB]: selectExecutiveSummaryDocument,
  [PER_RECORD_TAB]: selectPerRecordSummaryDocument,
  [MEDICATIONS_TAB]: selectMedicationDocument,
  [CURRENT_STATUS_ACCEPTED_TAB]: selectCurrentStatusAcceptedDocument,
  [CURRENT_STATUS_DENIED_TAB]: selectCurrentStatusDeniedDocument,
  [WARNINGS_ACCEPTED_TAB]: selectWarningsAcceptedDocument,
  [WARNINGS_DENIED_TAB]: selectWarningsDeniedDocument,
  [COST_MITIGATIONS_ACCEPTED_TAB]: selectCostMitigationsAcceptedDocument,
  [COST_MITIGATIONS_DENIED_TAB]: selectCostMitigationsDeniedDocument,
};

const getSelectedInsightDocument = ({
  activeDocumentGroupId,
  insightMetadata,
  shouldFetchInsightMetadata,
  tabId,
}: {
  activeDocumentGroupId?: number;
  insightMetadata: InsightMetadataRow[];
  shouldFetchInsightMetadata: boolean;
  tabId: TabId;
}): SelectedInsightDocument => {
  if (!shouldFetchInsightMetadata) {
    return EMPTY_INSIGHT_DOCUMENT;
  }

  const selectInsightDocument = insightDocumentSelectors[tabId];
  return selectInsightDocument
    ? selectInsightDocument({ activeDocumentGroupId, insightMetadata })
    : EMPTY_INSIGHT_DOCUMENT;
};

/*
selects the editable insight document for summary/medication tabs
and provides shared draft + blur-save behavior
*/
export const useAIInsightDocument = ({
  metadocId,
  tabId,
  activeDocumentGroupId,
  shouldFetch,
}: UseAIInsightDocumentArgs): UseAIInsightDocumentReturn => {
  const metadataType =
    tabId === EXECUTIVE_SUMMARY_TAB
      ? "executive_summary"
      : tabId === PER_RECORD_TAB
        ? "summary"
        : isMedicationTab(tabId)
          ? "medication"
          : isOtherSummaryTab(tabId)
            ? "other_summary"
            : undefined;
  const shouldFetchInsightMetadata = shouldFetch && !!metadataType;
  const shouldFetchRef = useRef(shouldFetchInsightMetadata);
  const [cacheVersion, setCacheVersion] = useState(0);

  // Force refetch when switching back to other-summary tab
  useEffect(() => {
    if (
      !shouldFetchRef.current &&
      shouldFetchInsightMetadata &&
      isOtherSummaryTab(tabId)
    ) {
      // Switching from hidden to visible for other-summary tab, bust the cache
      setCacheVersion((v) => v + 1);
    }
    shouldFetchRef.current = shouldFetchInsightMetadata;
  }, [shouldFetchInsightMetadata, tabId]);

  const {
    data: insightMetadata = [],
    isLoading,
    isFetching,
  } = useGetMetadocMetadataQuery(
    {
      metadocId,
      type: metadataType,
      _cacheVersion: isOtherSummaryTab(tabId) ? cacheVersion : undefined,
    },
    {
      skip: !shouldFetchInsightMetadata,
      // force a fresh read whenever an ai insight tab subscribes so we don't render stale empty metadata that was cached before ai writes complete.
      refetchOnMountOrArgChange: true,
    },
  );
  const [updateMetadocMetadata] = useUpdateMetadocMetadataMutation();
  const [createMetadocMetadata, { isLoading: isCreating }] =
    useCreateMetadocMetadataMutation();
  const [editorValue, setEditorValue] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [optimisticMetadataId, setOptimisticMetadataId] = useState<
    null | number
  >(null);
  const lastSavedValueRef = useRef("");
  const lastSavedDateRef = useRef("");
  const selectedMetadataKeyRef = useRef("");
  // Holds the last non-null effectiveMetadataId. When shouldFetch flips false on a tab switch, effectiveMetadataId immediately becomes null in that render,
  // but Tiptap fires onBlur synchronously during DOM removal (commit phase) — after the render but before effects. This ref lets onBlurSave still reach the real ID.
  const lastKnownMetadataIdRef = useRef<number | null>(null);

  const { content, metadataId, rawValue, date } = useMemo(
    () =>
      getSelectedInsightDocument({
        activeDocumentGroupId,
        insightMetadata,
        shouldFetchInsightMetadata,
        tabId,
      }),
    [insightMetadata, tabId, activeDocumentGroupId, shouldFetchInsightMetadata],
  );
  const effectiveMetadataId = metadataId ?? optimisticMetadataId;
  // Keep the ref updated every render so the blur handler always sees a valid ID.
  if (effectiveMetadataId !== null) {
    lastKnownMetadataIdRef.current = effectiveMetadataId;
  }
  const canCreate =
    (tabId === PER_RECORD_TAB &&
      typeof activeDocumentGroupId === "number" &&
      effectiveMetadataId === null) ||
    ((tabId === CURRENT_STATUS_ACCEPTED_TAB ||
      tabId === CURRENT_STATUS_DENIED_TAB) &&
      activeDocumentGroupId === undefined &&
      effectiveMetadataId === null);

  useEffect(() => {
    if (metadataId !== null && optimisticMetadataId !== null) {
      setOptimisticMetadataId(null);
    }
  }, [metadataId, optimisticMetadataId]);

  useEffect(() => {
    const selectedMetadataKey = `${tabId}-${effectiveMetadataId ?? "none"}`;
    const normalizedContent = content ?? "";
    const normalizedDate = date ?? "";

    // If metadata key changed (tab or metadata ID), reset to fetched content and date
    if (selectedMetadataKeyRef.current !== selectedMetadataKey) {
      selectedMetadataKeyRef.current = selectedMetadataKey;
      setEditorValue(normalizedContent);
      lastSavedValueRef.current = normalizedContent;
      setDateValue(normalizedDate);
      lastSavedDateRef.current = normalizedDate;
      return;
    }

    // Only sync fetched content if there are no unsaved changes (i.e., editor shows exactly what was last saved to the backend)
    if (
      !isFetching &&
      editorValue === lastSavedValueRef.current &&
      normalizedContent !== lastSavedValueRef.current
    ) {
      setEditorValue(normalizedContent);
      lastSavedValueRef.current = normalizedContent;
    }

    // Only sync fetched date if there are no unsaved date changes
    if (
      !isFetching &&
      dateValue === lastSavedDateRef.current &&
      normalizedDate !== lastSavedDateRef.current
    ) {
      setDateValue(normalizedDate);
      lastSavedDateRef.current = normalizedDate;
    }
  }, [
    content,
    date,
    effectiveMetadataId,
    tabId,
    editorValue,
    dateValue,
    isLoading,
    isFetching,
  ]);

  const save = useCallback(
    async (metadataIdToSave: number, valueToSave: string) => {
      try {
        // For other_summary types, update the "text" field in the JSON
        let finalValue: string | Record<string, unknown> = valueToSave;
        if (isOtherSummaryTab(tabId) && rawValue) {
          try {
            const parsed = parseMetadataValue(rawValue);
            if (
              typeof parsed === "object" &&
              parsed !== null &&
              !Array.isArray(parsed)
            ) {
              // Store Tiptap JSON directly to preserve paragraph formatting
              finalValue = {
                ...parsed,
                text: valueToSave,
              };
            }
          } catch {
            // If parsing fails, just use the value as-is
            finalValue = valueToSave;
          }
        }

        await updateMetadocMetadata({
          metadataId: metadataIdToSave,
          data: { value: finalValue },
        }).unwrap();
        // Cache invalidation is handled by RTK Query's invalidatesTags mechanism
        // No manual refetch needed - it will cause race conditions with the sync effect
      } catch (error) {
        console.error("Failed to update metadata:", error);
        KaboodleToaster.error(
          "Failed to save AI summary content. Please try again.",
        );
        throw error;
      }
    },
    [updateMetadocMetadata, tabId, rawValue],
  );

  const onBlurSave = useCallback(async () => {
    // Fall back to lastKnownMetadataIdRef when shouldFetch just flipped false (effectiveMetadataId is null in that render but the ID is still valid).
    const idToUse = effectiveMetadataId ?? lastKnownMetadataIdRef.current;
    if (!idToUse) {
      return;
    }

    if (editorValue === lastSavedValueRef.current) {
      return;
    }

    await save(idToUse, editorValue);
    lastSavedValueRef.current = editorValue;
  }, [effectiveMetadataId, editorValue, save]);

  const onDateBlurSave = useCallback(async () => {
    if (!effectiveMetadataId) {
      return;
    }

    if (dateValue === lastSavedDateRef.current) {
      return;
    }

    try {
      await updateMetadocMetadata({
        metadataId: effectiveMetadataId,
        data: { date: dateValue || null },
      }).unwrap();
      lastSavedDateRef.current = dateValue;
    } catch (error) {
      console.error("Failed to update metadata date:", error);
      KaboodleToaster.error("Failed to save date. Please try again.");
      throw error;
    }
  }, [effectiveMetadataId, dateValue, updateMetadocMetadata]);

  const onCreate = useCallback(async () => {
    if (!canCreate || typeof activeDocumentGroupId !== "number") {
      return;
    }

    try {
      const getMetadataTypeData = () => {
        if (tabId === PER_RECORD_TAB) {
          return {
            type: "summary" as const,
            value: "",
          };
        }
        if (tabId === CURRENT_STATUS_ACCEPTED_TAB) {
          return {
            type: "other_summary" as const,
            value: {
              type: "current_status_of_treatment",
              text: "",
              condition_type: "accepted",
            },
          };
        }
        if (tabId === CURRENT_STATUS_DENIED_TAB) {
          return {
            type: "other_summary" as const,
            value: {
              type: "current_status_of_treatment",
              text: "",
              condition_type: "denied",
            },
          };
        }
        return { type: "summary" as const, value: "" };
      };

      const typeData = getMetadataTypeData();
      const createdMetadata = await createMetadocMetadata({
        data: {
          metadoc: metadocId,
          ...typeData,
          document_group: activeDocumentGroupId ?? null,
        },
      }).unwrap();

      setOptimisticMetadataId(createdMetadata.id);
      setEditorValue("");
      lastSavedValueRef.current = "";
    } catch (error) {
      console.error("Failed to create metadata:", error);
      KaboodleToaster.error("Failed to create document. Please try again.");
      throw error;
    }
  }, [
    activeDocumentGroupId,
    canCreate,
    createMetadocMetadata,
    metadocId,
    tabId,
    setEditorValue,
  ]);

  return {
    content,
    editorValue,
    date: date ?? null,
    dateValue,
    metadataId: effectiveMetadataId,
    isLoading,
    isFetching,
    isCreating,
    canCreate,
    onChange: setEditorValue,
    onBlurSave,
    onDateChange: setDateValue,
    onDateBlurSave,
    onCreate,
  };
};
